const React = require('react')
const { useEffect, useState } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const { Container, Col, Row, UncontrolledTooltip, Table } = require('reactstrap')
const { useFormatBytes, useFormatTimestampShort, useBlockchainData, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')

const { LoadingSpinner, CopyButton } = require('@components')
const { TableHeader, TableFooter } = require('@components/table')

require('@styles/pages/transactions/index.css')

const PendingTransactions = () => {
    const history = useHistory()
    const location = useLocation()
    const params = new URLSearchParams(location.search)

    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestampShort()
    const { network } = useNetworkConfig()
    const { getPendingTransactions, getBlocks } = useBlockchainData(network.name)

    const [page, setPage] = useState(Number(params.get('p')) || 1)
    const [limit, setLimit] = useState(Number(params.get('ps')) || 25)
    const [transactionsData, setTransactionsData] = useState()
    const [timestamp, setTimestamp] = useState()

    const [loading, setLoading] = useState(false)
    const [totalPages, setTotalPages] = useState(0)

    const isMobile = useResponsive(BREAKPOINTS.extraLarge)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const pageParam = Number(params.get('p'))
        const limitParam = Number(params.get('ps'))

        if (pageParam && pageParam > 0 && pageParam !== page) {
            setPage(pageParam)
        }

        if (limitParam && limitParam > 0 && limitParam !== limit) {
            setLimit(limitParam)
        }
    }, [location.search])

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)

                const [transactionsResponse, blocksResponse] = await Promise.all([
                    getPendingTransactions(page, limit),
                    getBlocks(1, 1)
                ])

                if (transactionsResponse && blocksResponse) {
                    setTransactionsData(transactionsResponse)
                    setTotalPages(transactionsResponse.totalPages)
                    setTimestamp(blocksResponse.items[0].timestamp)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [network, page, limit])

    return (
        <div className='page'>
            <Container>
                <Col>
                    <Row>
                        <div className='page-header'>
                            <text className='page-title'>Pending Transactions</text>
                            <div className='page-title-border' />
                        </div>
                    </Row>
                    <div
                        className='box table-section'
                        style={{ padding: transactionsData?.totalItems == 0 ? '0 24px' : '24px 24px 0' }}
                    >
                        {loading ? (
                            <div
                                className='d-flex justify-content-center align-items-center'
                                style={{ height: '300px' }}
                            >
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                {transactionsData?.totalItems > 0 && (
                                    <TableHeader
                                        title={`A total of ${transactionsData?.totalItems.toLocaleString()} pending txns found`}
                                        page={page}
                                        limit={limit}
                                        totalPages={totalPages}
                                    />
                                )}
                                <Table
                                    responsive
                                    style={
                                        isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}
                                    }
                                >
                                    <thead>
                                        <tr>
                                            <th>Transaction Hash</th>
                                            <th>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <span>Method</span>
                                                    <div
                                                        id='method-tooltip'
                                                        style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <i
                                                            className='fas fa-circle-question'
                                                            style={{
                                                                fontSize: '14px',
                                                                color: 'var(--grey-color)'
                                                            }}
                                                        />
                                                    </div>
                                                    <UncontrolledTooltip placement='top' target='method-tooltip'>
                                                        Function executed based on decoded input data. For unidentified
                                                        functions, method ID is displayed instead
                                                    </UncontrolledTooltip>
                                                </div>
                                            </th>
                                            <th>Nonce</th>
                                            <th>Last Seen</th>
                                            <th>Gas Limit</th>
                                            <th>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <span>Gas Price</span>
                                                    <div
                                                        id='gas-price-tooltip'
                                                        style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <i
                                                            className='fas fa-circle-question'
                                                            style={{
                                                                fontSize: '14px',
                                                                color: 'var(--grey-color)'
                                                            }}
                                                        />
                                                    </div>
                                                    <UncontrolledTooltip placement='top' target='gas-price-tooltip'>
                                                        For EIP 1559 tx, Max Fee Per Gas and Max Piority Fee Per Gas are
                                                        displayed
                                                    </UncontrolledTooltip>
                                                </div>
                                            </th>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!transactionsData?.totalItems ? (
                                            <tr>
                                                <td
                                                    colSpan='9'
                                                    style={{ textAlign: 'center', padding: '40px 0', height: '400px' }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '72px',
                                                            height: '72px',
                                                            backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            margin: '0 auto 12px'
                                                        }}
                                                    >
                                                        <i
                                                            className='fas fa-inbox'
                                                            style={{
                                                                fontSize: '24px',
                                                                color: 'var(--grey-color)'
                                                            }}
                                                        />
                                                    </div>
                                                    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                                        There are no matching entries
                                                    </div>
                                                    <div style={{ color: 'var(--grey-color)', marginTop: '4px' }}>
                                                        Please try again later
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            <>
                                                {transactionsData?.items.map((transaction, index) => {
                                                    return (
                                                        <>
                                                            <tr key={transaction.hash}>
                                                                <td>
                                                                    <span
                                                                        id={`tx-hash-${transaction.hash}`}
                                                                        className={`${network.name}-text-color cursor-pointer`}
                                                                        onClick={() =>
                                                                            history.push(
                                                                                `/${network.name}/tx/${transaction.hash}`
                                                                            )
                                                                        }
                                                                    >
                                                                        {formatBytes(transaction.hash, 12, 9, 0)}
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        placement='top'
                                                                        target={`tx-hash-${transaction.hash}`}
                                                                    >
                                                                        {transaction.hash}
                                                                    </UncontrolledTooltip>
                                                                    &nbsp;
                                                                    <CopyButton
                                                                        id={`copy-tx-hash-${transaction.hash}`}
                                                                        data={transaction.hash}
                                                                        text={'Copy Transaction Hash'}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        id={`method-id-${transaction.hash}`}
                                                                        className='method-id'
                                                                    >
                                                                        {transaction.functionName}
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        placement='top'
                                                                        target={`method-id-${transaction.hash}`}
                                                                    >
                                                                        {transaction.functionName}
                                                                    </UncontrolledTooltip>
                                                                </td>
                                                                <td>{transaction.nonce}</td>
                                                                <td>{formatTimestamp(timestamp)}</td>
                                                                <td>{transaction.gas}</td>
                                                                <td>
                                                                    {transaction.maxFeePerGas}&nbsp;|&nbsp;
                                                                    {transaction.maxPriorityFeePerGas}
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        id={`from-${transaction.hash}`}
                                                                        className={`${network.name}-text-color cursor-pointer`}
                                                                        onClick={() =>
                                                                            history.push(
                                                                                `/${network.name}/address/${transaction.from.address}`
                                                                            )
                                                                        }
                                                                    >
                                                                        {formatBytes(
                                                                            transaction.from.address,
                                                                            20,
                                                                            7,
                                                                            6
                                                                        )}
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        placement='top'
                                                                        target={`from-${transaction.hash}`}
                                                                    >
                                                                        {transaction.from.address}
                                                                    </UncontrolledTooltip>
                                                                    &nbsp;
                                                                    <CopyButton
                                                                        id={`copy-from-${transaction.hash}`}
                                                                        data={transaction.from.address}
                                                                        text={'Copy Address'}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        id={`to-${transaction.hash}`}
                                                                        className={`${network.name}-text-color cursor-pointer`}
                                                                        onClick={() =>
                                                                            history.push(
                                                                                `/${network.name}/address/${transaction.to.address}`
                                                                            )
                                                                        }
                                                                    >
                                                                        {formatBytes(transaction.to.address, 20, 7, 6)}
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        placement='top'
                                                                        target={`to-${transaction.hash}`}
                                                                    >
                                                                        {transaction.to.address}
                                                                    </UncontrolledTooltip>
                                                                    &nbsp;
                                                                    <CopyButton
                                                                        id={`copy-to-${transaction.hash}`}
                                                                        data={transaction.to.address}
                                                                        text={'Copy Address'}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    {(transaction.value / 10 ** 18).toLocaleString(
                                                                        undefined,
                                                                        {
                                                                            minimumFractionDigits: 0,
                                                                            maximumFractionDigits: 18
                                                                        }
                                                                    )}
                                                                    &nbsp;
                                                                    {network.data.token}
                                                                </td>
                                                            </tr>
                                                        </>
                                                    )
                                                })}
                                            </>
                                        )}
                                    </tbody>
                                </Table>
                                {transactionsData?.totalItems > 0 && (
                                    <TableFooter
                                        limit={limit}
                                        setLimit={setLimit}
                                        page={page}
                                        setPage={setPage}
                                        totalPages={totalPages}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </Col>
            </Container>
        </div>
    )
}

module.exports = PendingTransactions
